import { CSSProperties, useRef, useState } from 'react';

import './LandingScene.css';
import { useLandingPointer } from './useLandingMotion';

/**
 * The landing scene's decorative layers.
 *
 * Every petal is generated once, at module load, by a seeded PRNG, so every
 * load, StrictMode double-render and screenshot is byte-identical. The
 * component hands CSS twenty nodes and a per-petal bag of custom properties
 * (transform-only); it owns no animation of its own and the compositor does
 * the rest.
 */
const PETAL_COUNT = 20;

/** Deterministic 32-bit PRNG (mulberry32). A fixed seed keeps the field stable. */
const mulberry32 = (seed: number) => () => {
  seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

const between = (r: () => number, min: number, max: number) => min + r() * (max - min);

const PETALS: CSSProperties[] = (() => {
  const r = mulberry32(0x5eed);
  return Array.from({ length: PETAL_COUNT }, (_, index) => {
    // Spread across the full width with jitter, so no two columns line up.
    const x = ((index + 0.5 + (r() - 0.5) * 0.6) / PETAL_COUNT) * 100;
    const fall = between(r, 14, 26);
    return {
      '--auth-x': `${x.toFixed(2)}%`,
      '--auth-size': `${between(r, 6, 14).toFixed(2)}px`,
      '--auth-alpha': between(r, 0.07, 0.1).toFixed(3),
      '--auth-fall': `${fall.toFixed(2)}s`,
      '--auth-sway': `${between(r, 14, 34).toFixed(1)}px`,
      '--auth-sway-d': `${between(r, 3.5, 6).toFixed(2)}s`,
      '--auth-spin': `${between(r, 120, 420).toFixed(1)}deg`,
      // Negative, so the field is already populated on the first painted frame.
      '--auth-delay': `${(-r() * fall).toFixed(2)}s`,
      '--auth-tint': index % 3 === 2 ? 'var(--pink150)' : 'var(--pink300)',
    } as CSSProperties;
  });
})();

const LandingAtmosphere = () => {
  const scene = useRef<HTMLDivElement>(null);
  const [bloomed, setBloomed] = useState(false);
  useLandingPointer(scene);

  return (
    <div className="authAtmosphere" ref={scene} aria-hidden="true">
      {/* Two nodes per glow: the core owns the entrance, the wrapper owns the
          pointer parallax, so the two never fight over transform/opacity. */}
      <div className="authGlow authGlow--warm">
        <span className="authGlowCore" />
      </div>
      <div className="authGlow authGlow--cool">
        <span className="authGlowCore" />
      </div>
      <div className="authPetals">
        {PETALS.map((petal, index) => (
          <span className="authPetal" key={index} style={petal}>
            <span className="authPetalLeaf" />
          </span>
        ))}
      </div>
      {/* The one flourish: the lights come up once, then the node is removed so
          the resting page carries no extra composited layer. */}
      {!bloomed && <div className="authBloom" onAnimationEnd={() => setBloomed(true)} />}
      <div className="authSpotlight" />
    </div>
  );
};

export default LandingAtmosphere;
